import React from "react";
import { logout } from "../../app/common";

const variants = {
  notStaff: {
    title: "This is not a staff account",
    body:
      "You are signed in, but this account has no access to the admin portal. If you are a member of staff, sign in with the account your Super Admin created for you.",
    action: "signOut",
  },
  notPermitted: {
    title: "You do not have access to this page",
    body:
      "Your account does not hold the role this page requires. Ask a Super Admin to grant it, or go back to the pages you can use.",
    action: "home",
  },
  missingPermission: {
    title: "Your role does not include this page",
    body:
      "Ask a Super Admin to add the permission below to your role. Nobody can grant it to themselves, so it has to be a Super Admin.",
    action: "home",
  },
};

/**
 * Shown instead of a protected page when the gate refuses.
 *
 * Refusal has to say something. The portal used to render nothing at all, which looks identical to
 * a page that failed to load, so a customer who signed in saw a blank screen and a member of staff
 * missing a role saw the same blank screen. Neither could tell whether to sign in differently, ask
 * for a role, or report a bug.
 */
export default function AccessDenied({ variant = "notStaff", permission, permissions = [] }) {
  const { title, body, action } = variants[variant] ?? variants.notStaff;
  const missingPermissions = permissions.length > 0 ? permissions : permission ? [permission] : [];

  return (
    <div className="min-h-screen bg-page-gradient flex items-center justify-center px-6">
      <div className="panel rounded-2xl max-w-md w-full px-8 py-10 text-center">
        <div className="w-12 h-12 mx-auto rounded-full bg-amber-100 flex items-center justify-center">
          <svg
            className="w-6 h-6 text-amber-600"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.8}
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z"
            />
          </svg>
        </div>
        <h1 role="alert" className="mt-5 text-lg font-semibold text-theme">
          {title}
        </h1>
        <p className="mt-2 text-sm text-theme-secondary">{body}</p>
        {/* The exact key, because it is what a Super Admin ticks in the role editor. A friendly
            paraphrase would be kinder to read and useless to act on. */}
        {missingPermissions.length > 0 && (
          <p className="mt-3 font-mono text-sm text-theme break-all">
            {missingPermissions.join(", ")}
          </p>
        )}
        {action === "signOut" ? (
          <button
            type="button"
            onClick={logout}
            className="mt-6 px-4 py-2 text-sm font-medium text-white btn-cta rounded-lg"
          >
            Sign out
          </button>
        ) : (
          <a
            href="/home"
            className="mt-6 inline-block px-4 py-2 text-sm font-medium text-white btn-cta rounded-lg no-underline"
          >
            Back to home
          </a>
        )}
      </div>
    </div>
  );
}
